import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useDropzone } from 'react-dropzone';
import { MentionsInput, Mention } from 'react-mentions';


import { useAppContext } from '../AppContext';
import UniversalMediaRenderer from './UniversalMediaRenderer';
import HoverTooltip from './HoverTooltip';

function JournalEntryForm({ initialText = '', initialMedia = null, remixingWhisper = null }) {
    const { t, i18n } = useTranslation();
    const { userId, uploadFile, appFunctions, setMessage, showConfirmation, db, appId, handlePageChange } = useAppContext();
    const [content, setContent] = useState(initialText);
    const [tags, setTags] = useState('');
    const [mediaFiles, setMediaFiles] = useState([]);
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const mediaInputRef = useRef(null);
    const [postTarget, setPostTarget] = useState('public');
    const [myNexuses, setMyNexuses] = useState([]);
    const [isLoadingNexuses, setIsLoadingNexuses] = useState(true);
    const [vibe, setVibe] = useState(null);
    const [toneSuggestions, setToneSuggestions] = useState([]);
    const [showToneModal, setShowToneModal] = useState(false);
    const [isSensitive, setIsSensitive] = useState(false);
    const [showPollModal, setShowPollModal] = useState(false);
    const [pollQuestion, setPollQuestion] = useState('');
    const [pollOptions, setPollOptions] = useState(['', '']);
    const [isRecording, setIsRecording] = useState(false);
    const [audioChunks, setAudioChunks] = useState([]);
    const mediaRecorder = useRef(null);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleDateTime, setScheduleDateTime] = useState('');

    const [loadingStates, setLoadingStates] = useState({
        tone: false,
        tags: false,
        vibe: false,
    });

    const users = [
        { id: '1', display: 'John Doe' },
        { id: '2', display: 'Jane Smith' },
    ];

    const hashtags = [
        { id: 'inspiration', display: 'inspiration' },
        { id: 'dailythought', display: 'dailythought' },
    ];

    useEffect(() => {
        if (!userId) return;
        const q = query(collection(db, `artifacts/${appId}/public/data/nexuses`), where('memberIds', 'array-contains', userId));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMyNexuses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoadingNexuses(false);
        });
        return () => unsubscribe();
    }, [userId, db, collection, onSnapshot, query, where, appId]);

    useEffect(() => {
        if (initialMedia) {
            setMediaFiles([Object.assign(initialMedia, { preview: initialMedia.mediaUrl })]);
            setMediaPreviews([initialMedia.mediaUrl]);
        }
        if (remixingWhisper) {
            setContent(`"${remixingWhisper.content}" (remix of @${remixingWhisper.authorDisplayName || 'unknown'})`);
            if (remixingWhisper.mediaUrl) {
                setMediaFiles([Object.assign(remixingWhisper, { preview: remixingWhisper.mediaUrl })]);
                setMediaPreviews([remixingWhisper.mediaUrl]);
            }
        }
    }, [initialMedia, remixingWhisper]);

    const onDrop = useCallback(acceptedFiles => {
        if (mediaFiles.length + acceptedFiles.length > 4) {
            setMessage(t('journalEntryForm.maxFilesError'));
            return;
        }
        const newFiles = acceptedFiles.map(file => Object.assign(file, {
            preview: URL.createObjectURL(file)
        }));
        setMediaFiles(prev => [...prev, ...newFiles]);
        setMediaPreviews(prev => [...prev, ...newFiles.map(f => f.preview)]);
    }, [mediaFiles, setMessage, t]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: 'image/*', multiple: true, noClick: true });

    const handleAiTool = useCallback(async (tool) => {
        if (!content.trim()) {
            setMessage(t('journalEntryForm.aiToolNoContent'));
            return;
        }
        showConfirmation({
            message: t('journalEntryForm.aiToolConfirmation'),
            onConfirm: async () => {
                setLoadingStates(prev => ({ ...prev, [tool]: true }));
                const invokeAiHelper = httpsCallable(appFunctions, 'invokeWhisperAiHelper');
                try {
                    const result = await invokeAiHelper({ text: content, tool });
                    if (result.data.success) {
                        if (tool === 'improve_writing') {
                            setToneSuggestions(result.data.suggestions);
                            setShowToneModal(true);
                        } else if (tool === 'suggest_tags') {
                            setTags(result.data.tags.join(', '));
                        } else if (tool === 'analyze_mood') {
                            setVibe(result.data.vibe);
                        }
                    }
                } catch (error) {
                    console.error(`Error with AI tool '${tool}':`, error);
                    setMessage(t('journalEntryForm.aiToolError', { error: error.message }));
                } finally {
                    setLoadingStates(prev => ({ ...prev, [tool]: false }));
                }
            }
        });
    }, [content, appFunctions, setMessage, showConfirmation, t]);

    const handleFileSelect = (e) => {
        if (e.target.files) {
            if (mediaFiles.length + e.target.files.length > 4) {
                setMessage(t('journalEntryForm.maxFilesError'));
                return;
            }
            const newFiles = Array.from(e.target.files).map(file => Object.assign(file, {
                preview: URL.createObjectURL(file)
            }));
            setMediaFiles(prev => [...prev, ...newFiles]);
            setMediaPreviews(prev => [...prev, ...newFiles.map(f => f.preview)]);
        }
    };

    const removeMedia = (index) => {
        setMediaFiles(prev => prev.filter((_, i) => i !== index));
        setMediaPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleRecordAudio = async () => {
        if (isRecording) {
            mediaRecorder.current.stop();
            setIsRecording(false);
        } else {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorder.current = new MediaRecorder(stream);
            mediaRecorder.current.ondataavailable = (e) => {
                setAudioChunks(prev => [...prev, e.data]);
            };
            mediaRecorder.current.onstop = () => {
                const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                const audioUrl = URL.createObjectURL(audioBlob);
                const audioFile = new File([audioBlob], `recording-${Date.now()}.webm`, { type: 'audio/webm' });
                const newAudioFile = Object.assign(audioFile, { preview: audioUrl });
                setMediaFiles(prev => [...prev, newAudioFile]);
                setMediaPreviews(prev => [...prev, audioUrl]);
                setAudioChunks([]);
            };
            mediaRecorder.current.start();
            setIsRecording(true);
        }
    };

    const handleCreatePoll = () => {
        // Logic to add the poll to the whisper
        setShowPollModal(false);
    };

    const handleScheduleSubmit = async () => {
        if (!content.trim() && mediaFiles.length === 0) {
            setMessage(t('journalEntryForm.noContentOrMedia'));
            return;
        }
        if (!scheduleDateTime) {
            setMessage(t('journalEntryForm.noScheduleDateTime'));
            return;
        }

        setIsSubmitting(true);
        try {
            const scheduleWhisper = httpsCallable(appFunctions, 'scheduleWhisper');
            await scheduleWhisper({
                content: content.trim(),
                tags: tags.split(',').map(t => t.trim()).filter(Boolean),
                mediaFiles,
                vibe,
                language: i18n.language,
                sensitive: isSensitive,
                remixOf: remixingWhisper ? remixingWhisper.id : null,
                poll: pollQuestion && pollOptions.filter(Boolean).length >= 2 ? { question: pollQuestion, options: pollOptions.filter(Boolean) } : null,
                scheduledAt: new Date(scheduleDateTime),
                postTarget,
            });

            setContent(''); setTags(''); setMediaFiles([]); setMediaPreviews([]);
            if (mediaInputRef.current) mediaInputRef.current.value = "";
            setPostTarget('public'); setVibe(null); setIsSensitive(false); setPollQuestion(''); setPollOptions(['', '']);
            setShowScheduleModal(false);
            setMessage(t('journalEntryForm.scheduleSuccess'));
        } catch (error) {
            console.error("Error scheduling post:", error);
            setMessage(t('journalEntryForm.scheduleError', { error: error.message }));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!content.trim() && mediaFiles.length === 0) {
            setMessage(t('journalEntryForm.noContentOrMedia'));
            return;
        }
        setIsSubmitting(true);

        try {
            let mediaUrls = [];
            let mediaPaths = [];

            for (const file of mediaFiles) {
                const fileExtension = file.name.split('.').pop();
                const mediaPath = `entries/${userId}/${Date.now()}.${fileExtension}`;
                const mediaUrl = await uploadFile(file, mediaPath, setUploadProgress);
                mediaUrls.push(mediaUrl);
                mediaPaths.push(mediaPath);
            }

            const tagsArray = tags.split(',').map(t => t.trim()).filter(Boolean);
            const payload = {
                content: content.trim(),
                tags: tagsArray,
                mediaUrls: mediaUrls.length > 0 ? mediaUrls : null,
                mediaPaths: mediaPaths.length > 0 ? mediaPaths : null,
                vibe,
                language: i18n.language,
                sensitive: isSensitive,
                remixOf: remixingWhisper ? remixingWhisper.id : null,
                poll: pollQuestion && pollOptions.filter(Boolean).length >= 2 ? { question: pollQuestion, options: pollOptions.filter(Boolean) } : null,
            };

            if (postTarget === 'public') {
                const createWhisper = httpsCallable(appFunctions, 'createWhisper');
                const result = await createWhisper(payload);
                setMessage(t('journalEntryForm.postSuccess', { reward: result.data.reward }));
            } else {
                const postToNexus = httpsCallable(appFunctions, 'postToNexus');
                await postToNexus({ ...payload, nexusId: postTarget });
                setMessage(t('journalEntryForm.postToNexusSuccess'));
                handlePageChange('nexus', { nexusId: postTarget });
            }

            setContent(''); setTags(''); setMediaFiles([]); setMediaPreviews([]);
            if (mediaInputRef.current) mediaInputRef.current.value = "";
            setPostTarget('public'); setVibe(null); setIsSensitive(false); setPollQuestion(''); setPollOptions(['', '']);

        } catch (error) {
            console.error("Error creating post:", error);
            setMessage(t('journalEntryForm.postError', { error: error.message }));
        } finally {
            setIsSubmitting(false);
        }
    }, [content, tags, mediaFiles, userId, appFunctions, uploadFile, setMessage, postTarget, handlePageChange, vibe, t, i18n.language, isSensitive, remixingWhisper, pollQuestion, pollOptions]);

    const ToneModal = ({ suggestions, onSelect, onClose }) => (
        <div className="tone-modal-overlay">
            <div className="tone-modal-content">
                <h3 className="text-xl font-bold text-center mb-4 text-indigo-300 font-playfair">{t('journalEntryForm.toneModalTitle')}</h3>
                <div className="space-y-3">
                    {suggestions.map((suggestion, index) => (
                        <button key={index} onClick={() => onSelect(suggestion)} className="tone-suggestion-item">
                            <p className="italic">"{suggestion}"</p>
                        </button>
                    ))}
                </div>
                <button onClick={onClose} className="w-full mt-4 px-4 py-2 bg-gray-600 text-white font-bold rounded-full hover:bg-gray-700">{t('journalEntryForm.cancel')}</button>
            </div>
        </div>
    );

    const ScheduleModal = () => (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50" onClick={() => setShowScheduleModal(false)}>
            <div className="bg-gray-900/80 p-6 rounded-lg shadow-glow max-w-md w-full border border-purple-500/50" onClick={e => e.stopPropagation()}>
                <h3 className="text-xl font-bold text-center mb-4 text-indigo-300 font-playfair">{t('journalEntryForm.scheduleTitle')}</h3>
                <input type="datetime-local" value={scheduleDateTime} onChange={e => setScheduleDateTime(e.target.value)} className="w-full bg-gray-800 text-white py-2 px-3 border border-gray-600 rounded-md focus:outline-none mb-4" />
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={() => setShowScheduleModal(false)} className="px-4 py-2 bg-gray-600 text-white font-bold rounded-full hover:bg-gray-700">{t('journalEntryForm.cancel')}</button>
                    <button onClick={handleScheduleSubmit} className="px-4 py-2 bg-indigo-600 text-white font-bold rounded-full hover:bg-indigo-700">{t('journalEntryForm.schedule')}</button>
                </div>
            </div>
        </div>
    );

    const PollModal = () => (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50" onClick={() => setShowPollModal(false)}>
            <div className="bg-gray-900/80 p-6 rounded-lg shadow-glow max-w-md w-full border border-purple-500/50" onClick={e => e.stopPropagation()}>
                <h3 className="text-xl font-bold text-center mb-4 text-indigo-300 font-playfair">{t('journalEntryForm.createPoll')}</h3>
                <input type="text" placeholder={t('journalEntryForm.pollQuestion')} value={pollQuestion} onChange={e => setPollQuestion(e.target.value)} className="w-full bg-gray-800 text-white py-2 px-3 border border-gray-600 rounded-md focus:outline-none mb-4" />
                {pollOptions.map((option, index) => (
                    <input key={index} type="text" placeholder={`Option ${index + 1}`} value={option} onChange={e => {
                        const newOptions = [...pollOptions];
                        newOptions[index] = e.target.value;
                        setPollOptions(newOptions);
                    }} className="w-full bg-gray-800 text-white py-2 px-3 border border-gray-600 rounded-md focus:outline-none mb-2" />
                ))}
                <div className="flex justify-between mt-2">
                    <button onClick={() => setPollOptions([...pollOptions, ''])} disabled={pollOptions.length >= 4} className="text-sm text-blue-400 hover:underline">{t('journalEntryForm.addOption')}</button>
                    <button onClick={() => setPollOptions(pollOptions.slice(0, -1))} disabled={pollOptions.length <= 2} className="text-sm text-red-400 hover:underline">{t('journalEntryForm.removeOption')}</button>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={() => setShowPollModal(false)} className="px-4 py-2 bg-gray-600 text-white font-bold rounded-full hover:bg-gray-700">{t('journalEntryForm.cancel')}</button>
                    <button onClick={handleCreatePoll} className="px-4 py-2 bg-indigo-600 text-white font-bold rounded-full hover:bg-indigo-700">{t('journalEntryForm.create')}</button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {showToneModal && (
                <ToneModal
                    suggestions={toneSuggestions}
                    onSelect={(suggestion) => {
                        setContent(suggestion);
                        setShowToneModal(false);
                    }}
                    onClose={() => setShowToneModal(false)}
                />
            )}
            {showScheduleModal && <ScheduleModal />}
            {showPollModal && <PollModal />}
            <div className="whisper-forge-container">
                <h2 className="text-3xl font-bold text-center mb-6 text-blue-300 font-playfair">{t('journalEntryForm.title')}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div {...getRootProps()} className={`relative border-2 border-dashed rounded-lg p-4 ${isDragActive ? 'border-blue-500' : 'border-gray-600'}`}>
                        <input {...getInputProps()} />
                        <div className="relative">
                            <MentionsInput
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder={t('journalEntryForm.placeholder')}
                                className="mentions"
                                maxLength="280"
                            >
                                <Mention
                                    trigger="@"
                                    data={users}
                                    className="mentions__mention"
                                />
                                <Mention
                                    trigger="#"
                                    data={hashtags}
                                    className="mentions__mention"
                                />
                            </MentionsInput>
                            <span className={`absolute bottom-2 right-3 text-sm ${content.length > 260 ? 'text-red-500' : 'text-gray-400'}`}>
                                {content.length} / 280
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-4">
                            {mediaPreviews.map((preview, index) => (
                                <div key={index} className="relative">
                                    <img src={preview} alt={`preview ${index}`} className="w-24 h-24 object-cover rounded-lg" />
                                    <button type="button" onClick={() => removeMedia(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                                        <LucideIcons.X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {isDragActive && <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center"><p className="text-white font-bold">Drop media to attach</p></div>}
                    </div>
                    <div className="forge-controls">
                        <HoverTooltip text={t('journalEntryForm.improveWritingTooltip')}><button type="button" onClick={() => handleAiTool('improve_writing')} disabled={loadingStates.tone || !content.trim()} className="forge-ai-button">{loadingStates.tone ? <div className="action-spinner" /> : <LucideIcons.Wand2 size={20} />}</button></HoverTooltip>
                        <HoverTooltip text={t('journalEntryForm.suggestTagsTooltip')}><button type="button" onClick={() => handleAiTool('suggest_tags')} disabled={loadingStates.tags || !content.trim()} className="forge-ai-button">{loadingStates.tags ? <div className="action-spinner" /> : <LucideIcons.Tags size={20} />}</button></HoverTooltip>
                        <HoverTooltip text={t('journalEntryForm.analyzeMoodTooltip')}><button type="button" onClick={() => handleAiTool('analyze_mood')} disabled={loadingStates.vibe || !content.trim()} className="forge-ai-button">{loadingStates.vibe ? <div className="action-spinner" /> : <LucideIcons.Smile size={20} />}</button></HoverTooltip>
                    </div>
                    {vibe && <p className="vibe-display">{t('journalEntryForm.mood', { vibe })}</p>}
                    <input type="text" id="journalTags" className="w-full py-2 px-4 bg-gray-800 text-white rounded-full border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none" placeholder={t('journalEntryForm.tagsPlaceholder')} value={tags} onChange={(e) => setTags(e.target.value)} />
                    <div className="flex items-center">
                        <input type="checkbox" id="sensitiveContent" name="sensitiveContent" checked={isSensitive} onChange={(e) => setIsSensitive(e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" />
                        <label htmlFor="sensitiveContent" className="ml-2 block text-sm text-gray-400">{t('journalEntryForm.sensitiveContent')}</label>
                    </div>
                    <div className="forge-options flex flex-col sm:flex-row items-center justify-between gap-4 p-3 bg-black/20 rounded-lg">
                        <select id="postTarget" value={postTarget} onChange={(e) => setPostTarget(e.target.value)} className="w-full sm:w-auto bg-gray-800 text-white py-2 px-3 border border-gray-600 rounded-md focus:outline-none" disabled={isLoadingNexuses}>
                            <option value="public">{t('journalEntryForm.postToPublicFeed')}</option>
                            {myNexuses.map(nexus => <option key={nexus.id} value={nexus.id}>{t('journalEntryForm.postToNexus', { nexusName: nexus.name })}</option>)}
                        </select>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                        <HoverTooltip text={t('journalEntryForm.attachMedia')}><button type="button" onClick={() => mediaInputRef.current.click()} className="ai-icon-button text-gray-400 hover:text-white"><LucideIcons.Paperclip size={22} /></button></HoverTooltip>
                        <HoverTooltip text={t('journalEntryForm.recordAudio')}><button type="button" onClick={handleRecordAudio} className="ai-icon-button text-gray-400 hover:text-white"><LucideIcons.Mic size={22} /></button></HoverTooltip>
                        <HoverTooltip text={t('journalEntryForm.createPoll')}><button type="button" onClick={() => setShowPollModal(true)} className="ai-icon-button text-gray-400 hover:text-white"><LucideIcons.BarChart3 size={22} /></button></HoverTooltip>
                        <input type="file" ref={mediaInputRef} onChange={handleFileSelect} className="hidden" accept="image/*" multiple />
                        <button type="button" onClick={() => setShowScheduleModal(true)} className="px-8 py-3 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition duration-300 flex items-center gap-2">
                            <LucideIcons.Clock size={18} />
                            {t('journalEntryForm.schedule')}
                        </button>
                        <button type="submit" disabled={isSubmitting} className="px-8 py-3 bg-indigo-600 text-white font-bold rounded-full hover:bg-indigo-700 transition duration-300 disabled:opacity-50 flex items-center gap-2">
                            {isSubmitting ? <div className="action-spinner" /> : <LucideIcons.Send size={18} />}
                            {isSubmitting ? t('journalEntryForm.posting') : t('journalEntryForm.postWhisper')}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
}

export default JournalEntryForm;
